/**
 * 근로자의 날(노동절, 5/1) 수당 계산
 * 설계문서: required/근로자의날.md §3·§4·§5
 *
 * 6가지 케이스 (기준: 시급 10,320 × 8h = 82,560)
 *  #1 일반 근무일                — 기본급 100% (본 모듈 무관)
 *  #2 시급제 + 결근              — 유급 100% (paidHolidayPay)
 *  #3 시급제 + 5인 이상 + 출근   — 유급 + 근무 + 가산 50%
 *  #4 월급제 + 5인 이상 + 출근   — 월급 + 근무 + 가산 50% (paidHolidayPay 0)
 *  #5 시급제 + 5인 미만 + 출근   — 유급 + 근무 (가산 X)
 *  #6 월급제 + 5인 미만 + 출근   — 월급 + 근무 (가산 X)
 */

import type { Employee, WorkSchedule } from '../types/employee';
import {
  MIN_WAGE_2026,
  MONTHLY_TO_HOURLY_DIVISOR,
  STANDARD_WORK_HOURS_PER_DAY,
  LABOR_DAY_PREMIUM_RATE,
} from '../constants/laborLaw';
import { breakTimeStringToHours } from './breakTime';
import { getHoursBetween } from './payrollCalculations';
import { useRatesStore } from '../store/ratesStore';

/** 노동절 수당 3컴포넌트 분해 (설계문서 §4) */
export interface LaborDayAllowanceBreakdown {
  /** 시급제 한정: effectiveRate × 8h. 결근해도 지급되는 유급분 */
  paidHolidayPay: number;
  /** 출근 시: effectiveRate × workedHours. 1차+2차 합산 시간의 실근무분 */
  workPay: number;
  /** 5인 이상 + 출근 시: workPay × 0.5 가산 */
  premiumPay: number;
  /** 위 셋의 합 */
  total: number;
  /** 1차+2차 합산 근무시간 (휴게시간 차감 후) */
  workedHours: number;
  isHourly: boolean;
  is5Plus: boolean;
  worked: boolean;
}

export const LABOR_DAY_BREAKDOWN_ZERO: LaborDayAllowanceBreakdown = {
  paidHolidayPay: 0,
  workPay: 0,
  premiumPay: 0,
  total: 0,
  workedHours: 0,
  isHourly: false,
  is5Plus: false,
  worked: false,
};

interface CalculateLaborDayBreakdownArgs {
  isHourly: boolean;
  is5Plus: boolean;
  effectiveRate: number;
  workedHours: number;
}

/**
 * 노동절 수당 분해 계산. 설계문서 §5.
 * 토글과 무관하게 항상 호출 가능 (말풍선·바텀시트·PDF에서 잠재액 노출용).
 */
export function calculateLaborDayBreakdown(
  args: CalculateLaborDayBreakdownArgs
): LaborDayAllowanceBreakdown {
  const { isHourly, is5Plus, effectiveRate, workedHours } = args;
  const worked = workedHours > 0;

  // 근로자의 날 가산률 (Firestore SSOT, default 0.5)
  const laborDayRate =
    useRatesStore.getState().current.labor?.overtime?.laborDay ?? LABOR_DAY_PREMIUM_RATE;

  const paidHolidayPay = isHourly ? effectiveRate * STANDARD_WORK_HOURS_PER_DAY : 0;
  const workPay = worked ? effectiveRate * workedHours : 0;
  const premiumPay = is5Plus && worked ? effectiveRate * workedHours * laborDayRate : 0;
  const total = paidHolidayPay + workPay + premiumPay;

  return {
    paidHolidayPay: Math.round(paidHolidayPay),
    workPay: Math.round(workPay),
    premiumPay: Math.round(premiumPay),
    total: Math.round(total),
    workedHours,
    isHourly,
    is5Plus,
    worked,
  };
}

/**
 * 직원 effectiveRate (시급) 산정. 설계문서 §5.1
 *  - 시급제: hourlyRate (없으면 MIN_WAGE_2026 fallback)
 *  - 월급제: monthlySalary / 209 (반올림)
 */
export function resolveEffectiveRate(employee: Employee): number {
  const labor = useRatesStore.getState().current.labor;
  const minWage = labor?.minWage?.hourly ?? MIN_WAGE_2026;
  const divisor = labor?.workhour?.monthlyToHourlyDivisor ?? MONTHLY_TO_HOURLY_DIVISOR;

  if (employee.isHourly) {
    return employee.hourlyRate && employee.hourlyRate > 0 ? employee.hourlyRate : minWage;
  }
  if (!employee.monthlySalary || employee.monthlySalary <= 0) return 0;
  return Math.round(employee.monthlySalary / divisor);
}

/** 5/1 (Labor Day) 판정 */
export function isLaborDay(date: Date): boolean {
  return date.getMonth() === 4 && date.getDate() === 1;
}

/** 해당 월에 5/1이 포함되는가 (= 5월인가) */
export function monthHasLaborDay(month: Date): boolean {
  return month.getMonth() === 4;
}

/**
 * 한 스케줄의 1차+2차 근무시간 합산 (휴게시간 차감).
 * payrollCalculations.calculateTotalWorkHours 의 단일 스케줄 버전.
 */
export function calculateScheduleWorkedHours(schedule: WorkSchedule): number {
  let hours = 0;
  if (schedule.workTime1 || schedule.workTime2) {
    if (schedule.workTime1) hours += getHoursBetween(schedule.workTime1.start, schedule.workTime1.end);
    if (schedule.workTime2) hours += getHoursBetween(schedule.workTime2.start, schedule.workTime2.end);
    hours -= breakTimeStringToHours(schedule.break_time);
  } else if (schedule.start_time && schedule.end_time) {
    const start = new Date(schedule.start_time);
    const end = new Date(schedule.end_time);
    if (end < start) end.setDate(end.getDate() + 1);
    hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    hours -= breakTimeStringToHours(schedule.break_time);
  }
  return Math.max(0, hours);
}

/** 노동절 케이스 표 (설계문서 §3·§10.4 LABOR_DAY_CASES) */
export const LABOR_DAY_CASES: ReadonlyArray<readonly [string, string, string, string, string]> = [
  ['일반 근무일 (기준)', '공통', '10,320 × 8h', '82,560원', '기본급 100%'],
  ['노동절 결근', '시급제', '10,320 × 8h × 1.0', '82,560원', '유급 100% (쉬어도)'],
  ['5인 이상 출근', '시급제', '10,320 × 8h × 2.5', '206,400원', '유급+근무+가산 50%'],
  ['5인 이상 출근', '월급제', '월급+10,320×8h×1.5', '월급+123,840원', '월급 외 근무·가산'],
  ['5인 미만 출근', '시급제', '10,320 × 8h × 2.0', '165,120원', '유급+근무 (가산 X)'],
  ['5인 미만 출근', '월급제', '월급+10,320×8h×1.0', '월급+82,560원', '월급 외 실근무'],
] as const;
